import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { useSucursalStore } from '@/stores/sucursal';

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'login',
    component: () => import('./views/LoginView.vue'),
    meta: { public: true },
  },
  {
    path: '/forgot',
    name: 'forgot',
    component: () => import('./views/ForgotPasswordView.vue'),
    meta: { public: true },
  },
  {
    path: '/reset',
    name: 'reset',
    component: () => import('./views/ResetPasswordView.vue'),
    meta: { public: true },
  },
  {
    path: '/cambiar-contrasena',
    name: 'change-password',
    component: () => import('./views/ChangePasswordView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/sucursales',
    name: 'sucursales',
    component: () => import('./views/SucursalesView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/modo',
    name: 'admin-mode',
    component: () => import('./views/AdminModeView.vue'),
    meta: { requiresAuth: true, requiresRole: 'admin' },
  },
  {
    path: '/app',
    component: () => import('./layouts/AppLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      { path: '', redirect: '/app/dashboard' },
      {
        path: 'admin',
        name: 'admin-dashboard',
        component: () => import('./views/AdminDashboardView.vue'),
      },
      {
        path: 'managers',
        name: 'managers',
        component: () => import('./views/ManagersView.vue'),
        meta: { requiresRole: 'admin' },
      },
      {
        path: 'dashboard',
        name: 'dashboard',
        component: () => import('./views/DashboardView.vue'),
      },
      {
        path: 'clientes',
        name: 'clientes',
        component: () => import('./views/ClientesView.vue'),
      },
      {
        path: 'historial',
        name: 'historial',
        component: () => import('./views/HistorialView.vue'),
      },
      {
        path: 'lavadores',
        name: 'lavadores',
        component: () => import('./views/LavadoresView.vue'),
      },
      {
        path: 'reportes',
        name: 'reportes',
        component: () => import('./views/ReportesView.vue'),
        meta: { requiresRole: 'admin' },
      },
      {
        path: 'bitacora',
        name: 'bitacora',
        component: () => import('./views/BitacoraView.vue'),
        meta: { requiresRole: 'admin' },
      },
      {
        path: 'catalogo',
        name: 'catalogo-admin',
        component: () => import('./views/CatalogoAdminView.vue'),
        meta: { requiresRole: 'admin' },
      },
      {
        path: 'cortes',
        name: 'cortes-historicos',
        component: () => import('./views/CortesHistoricosView.vue'),
        meta: { requiresRole: 'admin' },
      },
      {
        path: 'contingencia',
        name: 'contingencia',
        component: () => import('./views/ContingenciaView.vue'),
        meta: { requiresRole: 'admin' },
      },
      {
        path: 'orden/:id/detalle',
        name: 'orden-detalle',
        component: () => import('./views/orden/DetalleOrdenView.vue'),
      },
    ],
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
});

// ============================================================
// Guards de autenticación y autorización
// ============================================================

router.beforeEach(async (to) => {
  const auth = useAuthStore();
  const sucursalStore = useSucursalStore();

  if (to.meta.public) {
    if (to.name === 'login' && auth.isAuthenticated) {
      if (auth.isAdmin) return { name: 'admin-mode' };
      return auth.isManager && auth.sucursalesAsignadas.length !== 1
        ? { path: '/sucursales' }
        : { path: '/app/dashboard' };
    }
    return true;
  }

  if (!to.meta.requiresAuth) return true;

  if (!auth.isAuthenticated) {
    await auth.refresh();
  }

  if (auth.user?.mustChangePassword) {
    return to.name === 'change-password' ? true : { name: 'change-password' };
  }
  if (to.name === 'change-password') {
    return auth.isAdmin ? { name: 'admin-mode' } : { name: 'sucursales' };
  }

  if (!auth.isAuthenticated) {
    return { path: '/', query: { redirect: to.fullPath } };
  }

  if (to.meta.requiresRole && auth.user?.role !== to.meta.requiresRole) {
    return auth.isAdmin ? { name: 'admin-mode' } : { path: '/app/dashboard' };
  }

  if (auth.isAdmin) {
    if (to.name === 'admin-mode') return true;
    if (auth.modoAdmin === null) return { name: 'admin-mode' };
    if (auth.modoAdmin === 'admin') {
      return to.name === 'admin-dashboard' ||
        to.name === 'managers' ||
        to.name === 'reportes' ||
        to.name === 'bitacora' ||
         to.name === 'catalogo-admin' ||
         to.name === 'cortes-historicos' ||
         to.name === 'contingencia'
        ? true
        : { name: 'admin-dashboard' };
    }
    if (
      to.name === 'admin-dashboard' ||
      to.name === 'managers' ||
      to.name === 'reportes' ||
      to.name === 'bitacora' ||
       to.name === 'catalogo-admin' ||
       to.name === 'cortes-historicos' ||
       to.name === 'contingencia'
    )
      return { name: 'sucursales' };
    if (to.name === 'sucursales') return true;
    if (!sucursalStore.operacion) return { name: 'sucursales' };
    return true;
  }

  if (to.name === 'admin-dashboard' || to.name === 'admin-mode') {
    return { path: '/app/dashboard' };
  }

  if (auth.isManager && !sucursalStore.operacion && to.path !== '/sucursales') {
    return { path: '/sucursales' };
  }

  return true;
});
